import React from "react";
import { ArrowRight } from "lucide-react";

const HOME_URL = "https://marisdesk.com/home";
const LOGIN_URL = "https://marisdesk.com/desk/login";

const Eyebrow = ({ children, testid }) => (
  <span className="md-eyebrow" data-testid={testid}>
    {children}
  </span>
);

const Header = ({ onCtaClick }) => (
  <header
    className="w-full"
    style={{
      background: "var(--md-bg)",
      borderBottom: "1px solid var(--md-line)",
    }}
    data-testid="page-header"
  >
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between gap-4">
      <a
        href={HOME_URL}
        className="font-display text-[1.4rem] sm:text-[1.55rem] tracking-tight"
        style={{ color: "var(--md-ink)" }}
        data-testid="brand-wordmark"
      >
        Maris Desk
      </a>
      <div className="flex items-center gap-2 sm:gap-5">
        <button
          type="button"
          onClick={onCtaClick}
          className="md-btn-primary"
          data-testid="nav-cta"
        >
          Request a Review
        </button>
        <a
          href={LOGIN_URL}
          className="md-btn-ghost hidden sm:inline-flex"
          data-testid="nav-login"
        >
          Existing Client Login
        </a>
      </div>
    </div>
  </header>
);

const Hero = ({ onCtaClick }) => (
  <section
    className="relative"
    style={{ background: "var(--md-bg)" }}
    data-testid="hero-section"
  >
    <div className="max-w-5xl mx-auto px-6 lg:px-10 pt-10 sm:pt-12 lg:pt-14 pb-8 sm:pb-10 lg:pb-12">
      <div className="md-rise md-rise-1">
        <Eyebrow testid="hero-eyebrow">
          For hotels, villas & short-term rentals
        </Eyebrow>
      </div>
      <h1
        className="font-display mt-4 text-[2.1rem] sm:text-[2.5rem] lg:text-[3rem] leading-[1.08] max-w-3xl md-rise md-rise-2"
        style={{ color: "var(--md-ink)" }}
        data-testid="hero-headline"
      >
        Request a Guest Request Flow Review
      </h1>
      <p
        className="mt-4 max-w-2xl text-[1rem] sm:text-[1.05rem] leading-relaxed md-rise md-rise-3"
        style={{ color: "var(--md-ink-soft)" }}
        data-testid="hero-subtext"
      >
        A short operational review for hotels, villas, and short-term rental
        teams that want to see where guest requests, follow-ups, and shift
        handovers may be getting scattered.
      </p>
      <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-4 md-rise md-rise-4">
        <button
          type="button"
          onClick={onCtaClick}
          className="md-btn-primary"
          data-testid="hero-cta-primary"
        >
          Start the Review Request
          <ArrowRight size={18} />
        </button>
        <p
          className="text-sm"
          style={{ color: "var(--md-ink-mute)" }}
          data-testid="hero-microcopy"
        >
          No setup required · Built for small hospitality teams
        </p>
      </div>
    </div>
  </section>
);

const reviewItems = [
  "Request channels",
  "Ownership",
  "Follow-ups",
  "Shift handover",
  "Repeating issues",
  "Review risk",
];

const SectionContext = () => (
  <section
    className="relative py-10 sm:py-12 lg:py-14"
    style={{ background: "var(--md-bg-sand)" }}
    data-testid="section-context"
  >
    <div className="max-w-5xl mx-auto px-6 lg:px-10">
      <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-start">
        <div className="lg:col-span-7">
          <Eyebrow testid="context-eyebrow">A note before the workspace</Eyebrow>
          <h2
            className="font-display mt-4 text-[1.7rem] sm:text-[1.95rem] leading-[1.15]"
            style={{ color: "var(--md-ink)" }}
            data-testid="context-heading"
          >
            Start with the flow.
          </h2>
          <p
            className="mt-4 text-[0.98rem] leading-relaxed"
            style={{ color: "var(--md-ink-soft)" }}
            data-testid="context-body"
          >
            Before Maris Desk becomes a workspace, we look at how guest
            requests move through your team today — where they arrive, who
            owns them, how follow-ups happen, and what carries into the next
            shift.
          </p>
        </div>

        <div className="lg:col-span-5">
          <p
            className="text-[0.72rem] tracking-[0.18em] uppercase font-semibold"
            style={{ color: "var(--md-blue)" }}
            data-testid="review-heading"
          >
            What we look at
          </p>
          <ul
            className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-x-6 gap-y-2"
            data-testid="review-list"
          >
            {reviewItems.map((item, i) => (
              <li
                key={item}
                className="flex items-baseline gap-3 py-1.5"
                style={{ borderTop: i === 0 ? "none" : "1px solid var(--md-line)" }}
                data-testid={`review-item-${i}`}
              >
                <span
                  aria-hidden
                  className="inline-block h-1.5 w-1.5 rounded-full shrink-0"
                  style={{ background: "var(--md-blue)" }}
                />
                <span
                  className="text-[0.98rem]"
                  style={{ color: "var(--md-ink)" }}
                >
                  {item}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <p
        className="mt-8 sm:mt-9 max-w-3xl text-[0.98rem] leading-relaxed"
        style={{ color: "var(--md-ink-soft)" }}
        data-testid="receive-body"
      >
        You receive a simple review showing where requests may be getting
        lost, where ownership is unclear, and how Maris Desk could support a
        calmer daily guest desk.
      </p>
    </div>
  </section>
);

const propertyTypes = [
  "Small hotel",
  "Boutique hotel",
  "Villa",
  "Short-term rental",
  "Other",
];

const mainConcerns = [
  "Guest requests get missed",
  "Follow-ups are delayed",
  "Reviews mention service issues",
  "Shift handover is messy",
  "Requests come from too many places",
  "Not sure yet",
];

const intakeChannels = [
  "WhatsApp",
  "Email",
  "Phone",
  "Front desk notes",
  "Guest app",
  "Booking platform messages",
  "Walk-ins / verbal requests",
  "Other",
];

export const LeadForm = () => {
  const [form, setForm] = React.useState({
    name: "",
    property_name: "",
    email: "",
    property_type: "",
    website: "",
    main_concern: "",
    intake_channels: [],
    message: "",
  });
  const [submitting, setSubmitting] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);
  const [error, setError] = React.useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const toggleChannel = (channel) => {
    setForm((prev) => {
      const has = prev.intake_channels.includes(channel);
      return {
        ...prev,
        intake_channels: has
          ? prev.intake_channels.filter((c) => c !== channel)
          : [...prev.intake_channels, channel],
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (
      !form.name ||
      !form.property_name ||
      !form.email ||
      !form.property_type ||
      !form.main_concern
    ) {
      setError("Please complete the required fields.");
      return;
    }
    setSubmitting(true);
    try {
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      const res = await fetch(`${API}/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Submission failed");
      }
      setSubmitted(true);
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div
        className="md-card px-7 py-10 sm:px-10 sm:py-12 text-center"
        data-testid="form-thank-you"
      >
        <div
          aria-hidden
          className="mx-auto mb-6 inline-flex items-center justify-center"
          style={{
            height: 52,
            width: 52,
            borderRadius: 999,
            border: "1px solid var(--md-blue)",
            background: "var(--md-blue-tint)",
          }}
        >
          <svg viewBox="0 0 28 28" width="22" height="22" fill="none">
            <path
              d="M7 14.5l4.2 4.2L21 9"
              stroke="var(--md-blue)"
              strokeWidth="1.7"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <h3
          className="font-display text-[1.6rem] sm:text-[2rem] leading-tight"
          style={{ color: "var(--md-ink)" }}
          data-testid="thank-you-title"
        >
          Thank you — your Guest Request Flow Review request has been received.
        </h3>
        <p
          className="mt-5 max-w-xl mx-auto leading-relaxed"
          style={{ color: "var(--md-ink-soft)" }}
          data-testid="thank-you-message"
        >
          We'll review your submission and follow up within 1–2 business days.
        </p>
        <div className="md-divider my-9 mx-auto max-w-xs" />
        <p className="text-sm" style={{ color: "var(--md-ink-mute)" }}>
          In the meantime, you can reply to our follow-up email with any extra
          context about your daily guest desk.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="md-card p-7 sm:p-10"
      data-testid="lead-form"
    >
      <h3
        className="font-display text-[1.6rem] sm:text-[1.9rem] leading-tight"
        style={{ color: "var(--md-ink)" }}
        data-testid="form-title"
      >
        Request Your Guest Request Flow Review
      </h3>
      <p
        className="mt-3 text-sm"
        style={{ color: "var(--md-ink-mute)" }}
      >
        Tell us a little about your property. We'll write back with the next
        step — no setup required.
      </p>

      <div className="grid sm:grid-cols-2 gap-4 mt-6">
        <div>
          <label className="md-label" htmlFor="name">
            Name <span className="md-required">required</span>
          </label>
          <input
            id="name"
            type="text"
            className="md-input"
            value={form.name}
            onChange={set("name")}
            placeholder="Your full name"
            data-testid="input-name"
            required
          />
        </div>
        <div>
          <label className="md-label" htmlFor="property">
            Property / Hotel name{" "}
            <span className="md-required">required</span>
          </label>
          <input
            id="property"
            type="text"
            className="md-input"
            value={form.property_name}
            onChange={set("property_name")}
            placeholder="e.g. Casa Marea"
            data-testid="input-property"
            required
          />
        </div>
        <div>
          <label className="md-label" htmlFor="email">
            Email <span className="md-required">required</span>
          </label>
          <input
            id="email"
            type="email"
            className="md-input"
            value={form.email}
            onChange={set("email")}
            placeholder="you@property.com"
            data-testid="input-email"
            required
          />
        </div>
        <div>
          <label className="md-label" htmlFor="ptype">
            Property type <span className="md-required">required</span>
          </label>
          <select
            id="ptype"
            className="md-select"
            value={form.property_type}
            onChange={set("property_type")}
            data-testid="input-property-type"
            required
          >
            <option value="">Choose one…</option>
            {propertyTypes.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className="md-label" htmlFor="website">
            Website or booking page link
          </label>
          <input
            id="website"
            type="text"
            className="md-input"
            value={form.website}
            onChange={set("website")}
            placeholder="https://"
            data-testid="input-website"
          />
        </div>
        <div className="sm:col-span-2">
          <span className="md-label" id="intake-channels-label">
            Where do guest requests usually come in today?
          </span>
          <p
            className="text-xs -mt-1 mb-3"
            style={{ color: "var(--md-ink-mute)" }}
          >
            Select all that apply.
          </p>
          <div
            role="group"
            aria-labelledby="intake-channels-label"
            data-testid="input-intake-channels"
            className="grid grid-cols-1 sm:grid-cols-2 gap-2"
          >
            {intakeChannels.map((channel) => {
              const checked = form.intake_channels.includes(channel);
              const id = `intake-${channel
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, "-")}`;
              return (
                <label
                  key={channel}
                  htmlFor={id}
                  className="md-channel"
                  data-testid={`channel-${id}`}
                  data-checked={checked ? "true" : "false"}
                >
                  <input
                    id={id}
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleChannel(channel)}
                    className="md-channel-input"
                  />
                  <span className="md-channel-box" aria-hidden>
                    <svg
                      viewBox="0 0 14 14"
                      width="10"
                      height="10"
                      fill="none"
                    >
                      <path
                        d="M2.5 7.4l2.7 2.7L11.5 4"
                        stroke="currentColor"
                        strokeWidth="1.7"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                  <span className="md-channel-text">{channel}</span>
                </label>
              );
            })}
          </div>
        </div>
        <div className="sm:col-span-2">
          <label className="md-label" htmlFor="concern">
            Main concern <span className="md-required">required</span>
          </label>
          <select
            id="concern"
            className="md-select"
            value={form.main_concern}
            onChange={set("main_concern")}
            data-testid="input-main-concern"
            required
          >
            <option value="">Choose what feels closest…</option>
            {mainConcerns.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className="md-label" htmlFor="message">
            Message / notes
          </label>
          <textarea
            id="message"
            rows={5}
            className="md-textarea"
            value={form.message}
            onChange={set("message")}
            placeholder="Anything you'd like us to know about your daily guest desk."
            data-testid="input-message"
          />
        </div>
      </div>

      {error ? (
        <p
          className="mt-5 text-sm"
          style={{ color: "var(--destructive)" }}
          data-testid="form-error"
        >
          {error}
        </p>
      ) : null}

      <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <button
          type="submit"
          disabled={submitting}
          className="md-btn-primary"
          data-testid="form-submit"
        >
          {submitting ? "Sending…" : "Request My Review"}
          <ArrowRight size={18} />
        </button>
        <p
          className="text-xs sm:max-w-xs"
          style={{ color: "var(--md-ink-mute)" }}
        >
          No setup required · Replies typically within 1–2 business days.
        </p>
      </div>
    </form>
  );
};

const SectionForm = React.forwardRef((_props, ref) => (
  <section
    ref={ref}
    id="form"
    className="relative py-10 sm:py-12 lg:py-14"
    style={{ background: "var(--md-bg)" }}
    data-testid="section-form"
  >
    <div className="max-w-3xl mx-auto px-6 lg:px-10">
      <div className="mb-6">
        <Eyebrow testid="form-eyebrow">Request your review</Eyebrow>
      </div>
      <LeadForm />
    </div>
  </section>
));
SectionForm.displayName = "SectionForm";

const Footer = () => (
  <footer
    className="relative py-10 sm:py-12"
    style={{
      background: "var(--md-bg-sand)",
      borderTop: "1px solid var(--md-line)",
    }}
    data-testid="footer"
  >
    <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
      <p
        className="font-display text-[1.3rem] sm:text-[1.45rem] leading-snug"
        style={{ color: "var(--md-ink)" }}
        data-testid="footer-connection"
      >
        Powered by Maris Desk
      </p>
      <p
        className="mt-3 max-w-xl mx-auto text-[0.95rem] leading-relaxed"
        style={{ color: "var(--md-ink-soft)" }}
      >
        A calmer workspace for guest requests, follow-ups, daily service work,
        and manager visibility.
      </p>
      <a
        href={HOME_URL}
        className="md-btn-ghost mt-5"
        data-testid="footer-home-link"
      >
        Learn more about Maris Desk
        <ArrowRight size={16} />
      </a>
      <div className="md-divider mt-10 mx-auto max-w-md" />
      <div
        className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-x-5 gap-y-2 text-xs flex-wrap"
        style={{ color: "var(--md-ink-mute)" }}
        data-testid="footer-meta"
      >
        <a
          href="mailto:support@marisdesk.com"
          className="hover:underline"
          style={{ color: "var(--md-blue)" }}
          data-testid="footer-support-email"
        >
          support@marisdesk.com
        </a>
        <span aria-hidden style={{ opacity: 0.5 }}>·</span>
        <a
          href="/privacy"
          className="hover:underline"
          style={{ color: "var(--md-ink-soft)" }}
          data-testid="footer-privacy-link"
        >
          Privacy
        </a>
        <span aria-hidden style={{ opacity: 0.5 }}>·</span>
        <a
          href="/terms"
          className="hover:underline"
          style={{ color: "var(--md-ink-soft)" }}
          data-testid="footer-terms-link"
        >
          Terms
        </a>
      </div>
      <p
        className="mt-4 text-xs"
        style={{ color: "var(--md-ink-mute)" }}
      >
        © {new Date().getFullYear()} Maris Desk. The Guest Request Flow Review
        is offered without setup, fees, or obligation.
      </p>
    </div>
  </footer>
);

export default function RequestReview() {
  const formRef = React.useRef(null);
  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div data-testid="request-review-page" style={{ background: "var(--md-bg)" }}>
      <Header onCtaClick={scrollToForm} />
      <Hero onCtaClick={scrollToForm} />
      <SectionContext />
      <SectionForm ref={formRef} />
      <Footer />
    </div>
  );
}
